import React, { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight, MapPin, Clock, Shield, Zap, Sparkles } from "lucide-react";
import Seo, { breadcrumbJsonLd } from "@/components/Seo";
import {
  SERVICE_SEO,
  CITIES,
  INDUSTRIES,
  findProgPage,
} from "@/data/programmatic-seo";

// -----------------------------------------------------------------------------
// Content helpers — inject genuine per-combo uniqueness so Google doesn't flag
// the page set as template spam. Each helper returns 2–4 sentences keyed off
// (service.key, city.slug | industry.slug) so no two pages share copy.
// -----------------------------------------------------------------------------

const SERVICE_INTRO_BY_KEY = {
  mobile: "native-feel iOS and Android apps",
  "ai-websites": "AI-native websites with semantic search, personalisation, and generative content",
  "custom-ai": "private RAG pipelines, agents, and fine-tuned LLM copilots",
  software: "web platforms, APIs, and internal tools",
  seo: "technical SEO, programmatic content, and Core Web Vitals engineering",
  marketing: "full-funnel paid, lifecycle, and creative",
  saas: "multi-tenant SaaS platforms with auth, billing, and admin",
};

const cityWhyUs = (serviceKey, city) => {
  const base = SERVICE_INTRO_BY_KEY[serviceKey] || "software";
  return `Teams in ${city.display} pick Spin Labs for ${base} because ${city.localProof.charAt(0).toLowerCase()}${city.localProof.slice(1)}`;
};

const industryWhyUs = (serviceKey, industry) => {
  const base = SERVICE_INTRO_BY_KEY[serviceKey] || "software";
  return `For ${industry.display} teams we ship ${base} with a sharper brief: ${industry.challenge}`;
};

// Build FAQ entries per combo — each Q&A is composed dynamically so it reads
// naturally and gives Google a useful FAQPage signal.
const buildCityFaq = (service, city) => [
  {
    q: `Can Spin Labs really deliver ${service.title.toLowerCase()} for companies in ${city.display}?`,
    a: `Yes. ${city.localProof} Our ${city.timezone} overlap means ${city.localSignal}, so reviews and discovery sessions happen on your calendar — not ours.`,
  },
  {
    q: `How long does a ${service.title.toLowerCase()} project typically take for a ${city.display} client?`,
    a: `Most ${service.title.toLowerCase()} engagements run 6–12 weeks from kickoff to production. The first two weeks are discovery and architecture (usually done on-site for ${city.display} clients when geography allows), followed by 4–10 weeks of build in sprints you can observe weekly.`,
  },
  {
    q: `Do you work with both startups and enterprise teams in ${city.display}?`,
    a: `We work with seed-stage founders shipping their first production release and with Series B+ and enterprise teams replatforming legacy stacks. The engagement model changes — the engineering bar doesn't.`,
  },
  {
    q: `What does a ${service.title.toLowerCase()} engagement cost for a ${city.display} team?`,
    a: `${city.country === "India" ? "Budgets for Indian clients" : `${city.country} engagements`} typically range from ₹6 lakh to ₹40 lakh (or USD equivalents) depending on scope. We publish an honest brief-to-estimate turnaround of under 48 hours — no vanity discovery calls.`,
  },
  {
    q: `Is the work actually done by senior engineers, not juniors?`,
    a: `Every engagement is led by a senior engineer who has shipped multiple production systems in this category. We do not pyramid projects through juniors — the person on your discovery call is the person writing the code.`,
  },
];

const buildIndustryFaq = (service, industry) => [
  {
    q: `How does Spin Labs handle ${industry.display} compliance during a ${service.title.toLowerCase()} build?`,
    a: `We build with ${industry.regulations}-aware defaults from day one — encryption posture, audit trails, data residency, and minimal-PII architecture are baked in, not added at the end. We won't ship a ${industry.display} product that fails a compliance review.`,
  },
  {
    q: `What's the hardest part of shipping ${service.title.toLowerCase()} for a ${industry.display} team?`,
    a: `Usually it's this: ${industry.challenge} Our approach — ${industry.approach} — targets that gap directly rather than pretending every ${industry.display} product is a greenfield consumer app.`,
  },
  {
    q: `Do you have relevant ${industry.display} case studies?`,
    a: `Yes — representative results include: ${industry.metric}. We can walk through architecture, trade-offs, and outcomes on the first call.`,
  },
  {
    q: `Can you integrate with our existing ${industry.display} systems?`,
    a: `In most engagements we integrate with existing data warehouses, identity providers, partner APIs, and domain-specific systems. We do the integration work; you don't need to ship a clean-slate replatform to work with us.`,
  },
  {
    q: `Do you sign ${industry.display}-appropriate agreements (NDAs, DPAs, BAAs)?`,
    a: `Yes. Standard NDA, DPA, and — where applicable (e.g. healthcare/HIPAA) — BAA templates are ready before kickoff. Enterprise procurement loops are something we've run through many times.`,
  },
];

// JSON-LD builders ------------------------------------------------------------

const serviceJsonLd = ({ service, city, industry, url, faq }) => {
  const provider = {
    "@type": "Organization",
    "@id": "https://spinlabs.in/#organization",
    name: "Spin Labs",
    url: "https://spinlabs.in",
    logo: "https://spinlabs.in/favicon-512.png",
    sameAs: [],
  };

  const serviceNode = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: city
      ? `${service.title} in ${city.display}`
      : `${service.title} for ${industry.display}`,
    serviceType: service.title,
    description: city
      ? `${service.summary} Available to clients in ${city.display}, ${city.country}.`
      : `${service.summary} Purpose-built for ${industry.display} teams.`,
    provider,
    url,
    ...(city
      ? { areaServed: { "@type": "City", name: city.name, containedInPlace: { "@type": "Country", name: city.country } } }
      : { areaServed: { "@type": "AdministrativeArea", name: industry.display } }),
    ...(industry && industry.metric ? { award: industry.metric } : {}),
  };

  const faqNode = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumb = breadcrumbJsonLd([
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    {
      name: city
        ? `${service.title} in ${city.display}`
        : `${service.title} for ${industry.display}`,
      path: url.replace("https://spinlabs.in", ""),
    },
  ]);

  return [serviceNode, faqNode, breadcrumb];
};

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export default function ProgrammaticServicePage() {
  const { slug } = useParams();
  const page = useMemo(() => (slug ? findProgPage(slug) : null), [slug]);

  // Unknown slug → send them to the services hub rather than a dead end.
  if (!page) return <Navigate to="/services" replace />;

  const { service, city, industry, kind } = page;
  const path = `/services/${page.slug}`;
  const url = `https://spinlabs.in${path}`;

  const h1 = kind === "city"
    ? `${service.title} in ${city.display}`
    : `${service.title} for ${industry.display}`;

  const tagline = kind === "city"
    ? `${service.tagline}. Shipped for teams across ${city.display}.`
    : `${service.tagline}. Engineered for ${industry.display}.`;

  const introLead = kind === "city"
    ? cityWhyUs(service.key, city)
    : industryWhyUs(service.key, industry);

  const faq = kind === "city" ? buildCityFaq(service, city) : buildIndustryFaq(service, industry);

  const description = kind === "city"
    ? `${service.title} for teams in ${city.display}, ${city.country}. ${service.summary}`
    : `${service.title} for ${industry.display} teams. ${industry.challenge} ${service.summary.slice(0, 120)}`;

  const keywords = kind === "city"
    ? `${service.ogKeyword}, ${service.ogKeyword} ${city.name}, ${city.name} ${service.title.toLowerCase()}, ${service.title.toLowerCase()} in ${city.name}`
    : `${service.ogKeyword}, ${service.ogKeyword} for ${industry.name}, ${industry.name} ${service.title.toLowerCase()}`;

  // Related pages for internal linking — every page ships with ~8 outbound
  // links to the same service in sibling cities/industries, plus cross-links
  // to 3 other services in the same city/industry. Good for SEO (discovery,
  // link equity) and good for users.
  const relatedSameService = kind === "city"
    ? CITIES.filter((c) => c.slug !== city.slug).slice(0, 6).map((c) => ({
        label: `${service.title} in ${c.display}`,
        to: `/services/${service.slug}-in-${c.slug}`,
      }))
    : INDUSTRIES.filter((i) => i.slug !== industry.slug).slice(0, 6).map((i) => ({
        label: `${service.title} for ${i.display}`,
        to: `/services/${service.slug}-for-${i.slug}`,
      }));

  const relatedOtherServices = Object.values(SERVICE_SEO)
    .filter((s) => s.slug !== service.slug)
    .slice(0, 4)
    .map((s) => ({
      label: kind === "city"
        ? `${s.title} in ${city.display}`
        : `${s.title} for ${industry.display}`,
      to: kind === "city"
        ? `/services/${s.slug}-in-${city.slug}`
        : `/services/${s.slug}-for-${industry.slug}`,
    }));

  const jsonLd = serviceJsonLd({ service, city, industry, url, faq });

  return (
    <div data-testid="prog-service-page">
      <Seo
        title={h1}
        path={path}
        description={description}
        keywords={keywords}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative pt-36 pb-16 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="spotlight w-[500px] h-[500px] bg-[#00F0FF]/15 top-0 left-1/4" />
        <div className="spotlight w-[500px] h-[500px] bg-[#FF00FF]/15 top-10 right-1/4" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="tag-chip mb-6 inline-flex items-center gap-2">
              {kind === "city" ? <MapPin className="w-3 h-3" /> : <Zap className="w-3 h-3" />}
              {kind === "city" ? `${city.display} · ${city.country}` : `${industry.display} · Industry`}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-balance max-w-4xl">
              {kind === "city" ? (
                <>{service.title} in <span className="gradient-text">{city.display}</span></>
              ) : (
                <>{service.title} for <span className="gradient-text">{industry.display}</span></>
              )}
            </h1>
            <p className="mt-6 max-w-2xl text-white/70 leading-relaxed text-lg">{tagline}</p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" className="btn-primary">Start a project</Link>
              <Link to="/work" className="btn-secondary group">
                See our work
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Unique intro paragraph — this is what keeps Google from flagging the page set */}
      <section className="relative py-16 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <p className="text-xl text-white/80 leading-relaxed">
            {introLead}
          </p>
          <p className="mt-6 text-white/70 leading-relaxed">
            {service.summary}
          </p>
        </div>
      </section>

      {/* Context block — city meta OR industry challenge/approach */}
      <section className="relative py-16 bg-[#07070A] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {kind === "city" ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <InfoCard icon={MapPin} color="#00F0FF" label="Location" value={`${city.display}, ${city.region}`} detail={city.country} />
              <InfoCard icon={Clock} color="#FF00FF" label="Timezone overlap" value={city.timezone} detail={city.localSignal} />
              <InfoCard icon={Sparkles} color="#39FF14" label="How we work with you" value={city.localSignal} detail={`Coordinates: ${city.coordinates}`} />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="card-hover rounded-2xl p-8">
                <div className="font-mono-neo text-[10px] uppercase tracking-[0.2em] text-[#FF00FF] mb-3">The challenge</div>
                <p className="text-white/80 leading-relaxed">{industry.challenge}</p>
              </div>
              <div className="card-hover rounded-2xl p-8">
                <div className="font-mono-neo text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] mb-3">Our approach</div>
                <p className="text-white/80 leading-relaxed">{industry.approach}</p>
              </div>
              <div className="card-hover rounded-2xl p-8">
                <div className="font-mono-neo text-[10px] uppercase tracking-[0.2em] text-[#39FF14] mb-3 inline-flex items-center gap-2">
                  <Shield className="w-3 h-3" /> Regulatory posture
                </div>
                <p className="text-white/80 leading-relaxed">{industry.regulations}</p>
              </div>
              <div className="card-hover rounded-2xl p-8">
                <div className="font-mono-neo text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] mb-3">Representative outcome</div>
                <p className="text-white/80 leading-relaxed">{industry.metric}</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* What's included */}
      <section className="relative py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="tag-chip mb-4">What's included</div>
          <h2 className="font-display text-3xl lg:text-4xl font-black tracking-tighter max-w-3xl">
            A <span className="gradient-text">{service.title.toLowerCase()}</span> engagement, end to end
          </h2>
          <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {DELIVERABLES_BY_SERVICE[service.key].map((p) => (
              <li key={p} className="flex items-start gap-3 p-5 card-hover rounded-xl">
                <Check className="w-5 h-5 mt-0.5 flex-shrink-0 text-[#00F0FF]" />
                <span className="text-white/80 leading-relaxed">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ — the SEO gold. FAQPage JSON-LD is already in <Seo /> above. */}
      <section className="relative py-20 bg-[#07070A] border-y border-white/5">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="tag-chip mb-4">FAQ</div>
          <h2 className="font-display text-3xl lg:text-4xl font-black tracking-tighter max-w-3xl">
            Honest answers, upfront.
          </h2>
          <div className="mt-10 space-y-3">
            {faq.map((f) => (
              <details key={f.q} className="group card-hover rounded-xl p-6">
                <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                  <span className="font-display text-lg font-bold text-white">{f.q}</span>
                  <span className="font-mono-neo text-xs text-[#00F0FF] mt-1 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-4 text-white/70 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Internal linking — related programmatic pages */}
      <section className="relative py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <div className="tag-chip mb-4">More locations</div>
              <h3 className="font-display text-2xl lg:text-3xl font-black tracking-tighter mb-6">
                {kind === "city"
                  ? `${service.title} elsewhere`
                  : `${service.title} for more industries`}
              </h3>
              <ul className="space-y-2">
                {relatedSameService.map((r) => (
                  <li key={r.to}>
                    <Link to={r.to} className="flex items-center justify-between gap-3 p-3 rounded-lg hover:bg-white/5 border border-transparent hover:border-white/10 transition-all group">
                      <span className="text-white/80 group-hover:text-white">{r.label}</span>
                      <ArrowUpRight className="w-4 h-4 text-white/40 group-hover:text-[#00F0FF] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="tag-chip mb-4">Other services</div>
              <h3 className="font-display text-2xl lg:text-3xl font-black tracking-tighter mb-6">
                Other things we ship {kind === "city" ? `in ${city.display}` : `for ${industry.display} teams`}
              </h3>
              <ul className="space-y-2">
                {relatedOtherServices.map((r) => (
                  <li key={r.to}>
                    <Link to={r.to} className="flex items-center justify-between gap-3 p-3 rounded-lg hover:bg-white/5 border border-transparent hover:border-white/10 transition-all group">
                      <span className="text-white/80 group-hover:text-white">{r.label}</span>
                      <ArrowUpRight className="w-4 h-4 text-white/40 group-hover:text-[#00F0FF] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl lg:text-5xl font-black tracking-tighter">
            Ready to ship <span className="gradient-text">{service.title.toLowerCase()}</span>
            {kind === "city" ? ` in ${city.display}?` : ` for your ${industry.display} team?`}
          </h2>
          <p className="mt-6 text-white/60 max-w-2xl mx-auto">
            Brief to estimate in under 48 hours. Senior engineers, no juniors, no pyramid.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/contact" className="btn-primary">Start a project</Link>
            <Link to="/services" className="btn-secondary">All services</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoCard({ icon: Icon, color, label, value, detail }) {
  return (
    <div className="card-hover rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center border"
             style={{ borderColor: `${color}50`, background: `${color}10` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div className="font-mono-neo text-[10px] uppercase tracking-[0.2em]" style={{ color }}>{label}</div>
      </div>
      <div className="font-display text-xl font-bold text-white">{value}</div>
      {detail && <div className="mt-2 text-sm text-white/60">{detail}</div>}
    </div>
  );
}

// Per-service deliverable list — swaps by service.key so every page reads with
// a service-accurate scope (not a generic checklist).
const DELIVERABLES_BY_SERVICE = {
  mobile: [
    "Native iOS (Swift) and Android (Kotlin) or React Native builds",
    "Offline-first architecture, payment SDKs, push notifications",
    "App Store + Play Store submission, review handling, binaries signed",
    "CI/CD with TestFlight / internal testing tracks",
    "Analytics + crash reporting wired on day one",
    "Release-grade accessibility and localisation passes",
  ],
  "ai-websites": [
    "AI-native IA: semantic search, generative content, personalisation",
    "Vector store + RAG pipeline for knowledge-heavy sections",
    "Edge-CDN deploy with sub-200ms global TTFB",
    "Schema-rich SEO foundation (Organization, FAQ, HowTo, Review)",
    "Analytics + LLM cost dashboards",
    "Migration or integration with your existing CMS / Webflow / Shopify",
  ],
  "custom-ai": [
    "Architecture: RAG, fine-tuning, or multi-agent — decided, not defaulted",
    "Private deployment (your cloud / VPC / on-prem) — no data leaves",
    "Chunking, retrieval, and eval-set engineering",
    "Guardrails, prompt-injection defenses, jailbreak monitoring",
    "Cost observability + latency SLOs",
    "Handoff with runbook, eval set, and engineer onboarding doc",
  ],
  software: [
    "Clean-architecture backend (Python / Node / Go) + relational DB",
    "API design (REST or GraphQL) with OpenAPI spec",
    "React / Next.js frontend with design system",
    "Auth, RBAC, audit logs, tenant isolation if multi-tenant",
    "IaC (Terraform / Pulumi) and CI/CD out of the box",
    "Load / soak testing + observability (logs, metrics, traces)",
  ],
  seo: [
    "Full technical SEO audit with a prioritised backlog",
    "Schema rollout (Organization, Service, FAQ, HowTo, BreadcrumbList, Review)",
    "Programmatic SEO systems (like this one) with unique per-page data",
    "Core Web Vitals engineering — LCP, CLS, INP",
    "Internal linking refactor + topical clusters",
    "Monthly retainer with measurable KPIs (organic sessions, rankings, ICP fit)",
  ],
  marketing: [
    "Full-funnel strategy: paid, organic, lifecycle, creative",
    "Paid channel setup + creative production (static, motion, UGC)",
    "Lifecycle automation (email, SMS, in-product)",
    "Attribution model rebuild (first/last/linear/shapley, not just GA)",
    "Weekly experiment cadence with a shared scorecard",
    "Senior-operator delivery — no interns with a dashboard",
  ],
  saas: [
    "Multi-tenant architecture (row-level or schema-per-tenant)",
    "Auth (email, OAuth, SAML), billing (Stripe), invoicing",
    "Admin panel with impersonation, audit trails, usage metrics",
    "Customer dashboards + embedded analytics",
    "Webhooks, public API, rate-limiting",
    "Production-grade observability + SOC2-ready logging",
  ],
};
